import { useState, useEffect, useRef } from 'react';
import { coreAdvantagesContent } from '@/mocks/home';

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function CoreAdvantageSection() {
  const [activeTab, setActiveTab] = useState(0);
  const { ref: sectionRef, inView } = useInView<HTMLDivElement>();
  const items = coreAdvantagesContent.items;

  return (
    <section className="relative bg-background-50 py-16 md:py-24 overflow-hidden" ref={sectionRef}>
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
        {/* Title */}
        <div className={`mb-10 md:mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs md:text-sm font-semibold text-foreground-400 uppercase tracking-widest font-label">
            {coreAdvantagesContent.titleEn}
          </span>
          <h2 className="mt-1 text-2xl md:text-3xl lg:text-4xl font-black text-foreground-50">
            {coreAdvantagesContent.titleCn}
          </h2>
          <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
        </div>

        {/* Tab Layout */}
        <div className={`flex flex-col lg:flex-row gap-6 lg:gap-8 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left Tabs */}
          <div className="lg:w-64 xl:w-72 shrink-0">
            <div className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(index)}
                  className={`text-left px-4 py-3 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-300 shrink-0 lg:shrink ${
                    activeTab === index
                      ? 'bg-primary-500 text-foreground-50'
                      : 'bg-background-100 text-foreground-300 hover:bg-background-200/50'
                  }`}
                >
                  {item.tabTitle}
                </button>
              ))}
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1">
            {items.map((item, index) => (
              <div
                key={item.id}
                className={`${activeTab === index ? 'block' : 'hidden'}`}
              >
                <div className="flex flex-col md:flex-row gap-6 md:gap-8 bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8">
                  {/* Text */}
                  <div className="flex-1">
                    <h3 className="text-lg md:text-xl font-bold text-foreground-50 mb-4">
                      {item.title}
                    </h3>
                    <div className="text-sm text-foreground-300 leading-relaxed whitespace-pre-line space-y-2">
                      {item.desc.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                  {/* Image */}
                  <div className="md:w-[280px] lg:w-[320px] shrink-0">
                    <div className="rounded-lg overflow-hidden border border-background-200/50">
                      <img
                        src={item.image}
                        alt={item.tabTitle}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}